import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from '../components/common/Button';
import { Loader } from 'lucide-react';
import {
  useGetCourseDetailsQuery,
  useRetakeCourseMutation,
  useGetCourseReportQuery,
  useGetNextCourseDetailsQuery,
} from '../redux/apiSlice';

import goldMedal from '../assets/medal/Gold_Mastery_Medal.png';
import silverMedal from '../assets/medal/Silver_Mastery_Medal.png';
import bronzeMedal from '../assets/medal/Bronze_Mastery_Medal.png';

const CourseCompletion: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [isRetaking, setIsRetaking] = useState(false);
  const {
    data: courseData,
    isLoading: courseLoading,
    error: courseError,
  } = useGetCourseDetailsQuery(Number(courseId));
  const {
    data: courseReport,
    isLoading: reportLoading,
    error: reportError,
  } = useGetCourseReportQuery(Number(courseId));
  const [retakeCourse] = useRetakeCourseMutation();
  const medal = courseReport?.medal || '';
  const {
    data: nextCourseData,
    isLoading: nextCourseLoading,
    error: nextCourseError,
  } = useGetNextCourseDetailsQuery(Number(courseData?.course_id), {
    skip: !courseData,
  });
  console.log('nextCourseData', nextCourseData);
  const title = courseData?.course_name || 'Course';
  const isLastCourse = !nextCourseData;

  const formatFeedback = (feedback: string, medalType: string): string => {
    if (!feedback) return '';

    // Only replace the placeholder if it exists in the feedback
    if (feedback.includes('{Bronze or Silver}')) {
      const medal = medalType?.charAt(0)?.toUpperCase() + medalType?.slice(1);
      return feedback.replace('{Bronze or Silver}', medal);
    }

    return feedback;
  };

  const handleContinueLearning = () => {
    navigate('/courses');
  };

  const handleRetakeCourse = () => {
    // Set loading state
    setIsRetaking(true);

    // Call the retake course mutation
    retakeCourse(Number(courseId))
      .unwrap()
      .then((response) => {
        navigate('/courses/coding', {
          state: {
            courseId: Number(courseId),
            courseName: courseData?.course_name,
            progress: 0,
            duration: courseData?.duration,
            timeSpent: 0,
            language: courseData?.language.name,
          },
        });
      })
      .catch((error) => {
        console.error('Error resetting course:', error);
        setIsRetaking(false);
        // Navigate anyway in case of error
        navigate(`/course/${courseId}`);
      });
  };

  const handleRetakeNextCourse = () => {
    // Set loading state
    setIsRetaking(true);

    // Call the retake course mutation
    retakeCourse(Number(nextCourseData?.id))
      .unwrap()
      .then((response) => {
        navigate('/courses/coding', {
          state: {
            courseId: Number(nextCourseData?.id),
            courseName: nextCourseData?.course_name,
            progress: 0,
            duration: nextCourseData?.duration,
            timeSpent: 0,
            language: nextCourseData?.language.name,
            nextCourseId: undefined,
          },
        });
      })
      .catch((error) => {
        console.error('Error resetting course:', error);
        setIsRetaking(false);
        // Navigate anyway in case of error
        navigate(`/course/${nextCourseData?.id}`);
      });
  };

  const handleNextCourse = () => {
    if (nextCourseData?.detail?.course_completed) {
      return handleRetakeNextCourse();
    } else {
      navigate('/courses/coding', {
        state: {
          courseId: Number(nextCourseData?.id),
          courseName: nextCourseData?.course_name,
          progress: 0,
          duration: nextCourseData?.duration,
          timeSpent: 0,
          language: nextCourseData?.language.name,
          nextCourseId: undefined,
          lastAttemptedSectionId:
            nextCourseData?.detail?.last_incomplete_section_id,
          lastAttemptedQuestionId:
            nextCourseData?.detail?.last_incomplete_question_id,
          lastAttemptedQuestionAttempts:
            nextCourseData?.detail?.last_incomplete_question_attempts,
        },
      });
    }
  };

  // Function to render medal image based on medal type
  const renderMedalImage = () => {
    // Using image URLs for medals instead of Lucide icons for better appearance
    switch (medal.toLowerCase()) {
      case 'gold':
        return (
          <img
            src={goldMedal}
            alt="Gold Dragon Mastery Medal"
            className="h-44 w-44 object-contain"
          />
        );
      case 'silver':
        return (
          <img
            src={silverMedal}
            alt="Silver Dragon Mastery Medal"
            className="h-44 w-44 object-contain"
          />
        );
      case 'bronze':
        return (
          <img
            src={bronzeMedal}
            alt="Bronze Dragon Mastery Medal"
            className="h-44 w-44 object-contain"
          />
        );
      default:
        return '';
    }
  };

  // Function to render buttons based on medal type and course position
  const renderButtons = () => {
    const isBronzeOrSilver =
      medal.toLowerCase() === 'bronze' || medal.toLowerCase() === 'silver';

    // Use a blue button style similar to the provided image
    const buttonBaseClass =
      'font-semibold py-2 px-6 rounded-lg transition duration-300 mb-2 text-white';
    const primaryButtonClass = `bg-blue-800 hover:bg-blue-900 ${buttonBaseClass}`;

    if (!isLastCourse) {
      if (isBronzeOrSilver) {
        return (
          <div className="flex flex-col items-center w-full gap-3">
            <Button
              className={primaryButtonClass}
              onClick={handleRetakeCourse}
              disabled={isRetaking}
            >
              {isRetaking ? (
                <span className="flex items-center justify-center">
                  <Loader className="animate-spin mr-2 h-4 w-4" />
                  Resetting Course...
                </span>
              ) : (
                `Retake ${title}`
              )}
            </Button>
            {nextCourseData &&
              (!nextCourseData.detail?.course_completed ? (
                <Button
                  className={primaryButtonClass}
                  onClick={handleNextCourse}
                  disabled={isRetaking}
                >
                  Next Course
                </Button>
              ) : (
                <Button
                  className={primaryButtonClass}
                  onClick={handleRetakeNextCourse}
                  disabled={isRetaking}
                >
                  Next Course
                </Button>
              ))}
            <Button
              className={primaryButtonClass}
              onClick={handleContinueLearning}
              disabled={isRetaking}
            >
              Explore All Courses
            </Button>
          </div>
        );
      } else {
        // Gold medal, not last course
        return (
          <div className="flex flex-col items-center w-full gap-3">
            {
              nextCourseData && (
                //   !nextCourseData.detail?.course_completed ? (
                <Button
                  className={primaryButtonClass}
                  onClick={handleNextCourse}
                  disabled={isRetaking}
                >
                  Next Course
                </Button>
                // ) : (
                // <Button
                //   className={primaryButtonClass}
                //   onClick={handleRetakeNextCourse}
                //   disabled={isRetaking}
                // >
                //   Retake {nextCourseData?.course_name || 'Course'}
                // </Button>
              )
              // )
            }
            <Button
              className={primaryButtonClass}
              onClick={handleContinueLearning}
            >
              Go to My Courses
            </Button>
          </div>
        );
      }
    } else {
      // Last course
      if (isBronzeOrSilver) {
        return (
          <div className="flex flex-col items-center w-full gap-3">
            <Button
              className={primaryButtonClass}
              onClick={handleRetakeCourse}
              disabled={isRetaking}
            >
              {isRetaking ? (
                <span className="flex items-center justify-center">
                  <Loader className="animate-spin mr-2 h-4 w-4" />
                  Resetting Course...
                </span>
              ) : (
                'Retake the Course'
              )}
            </Button>
            <Button
              className={primaryButtonClass}
              onClick={handleContinueLearning}
              disabled={isRetaking}
            >
              Explore All Courses
            </Button>
          </div>
        );
      } else {
        // Gold medal, last course
        return (
          <Button
            className={primaryButtonClass}
            onClick={handleContinueLearning}
          >
            Go to My Courses
          </Button>
        );
      }
    }
  };

  if (courseLoading) {
    return (
      <div className="min-h-[80vh] flex-1 bg-gray-100 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800 mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (courseError) {
    return (
      <div className="min-h-[80vh] flex-1 bg-gray-100 flex justify-center items-center">
        <div className="text-center text-red-600">
          <p>Error loading course details. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex-1 bg-gray-100 flex justify-center items-center py-8">
      <div className="bg-white shadow-md rounded-lg max-w-lg w-full mx-4">
        <div className="p-8 text-center">
          <h1 className="text-2xl font-semibold text-gray-700 mb-2">
            Congratulations!
          </h1>
          <p className="mb-6 text-gray-700">
            You have successfully completed the <strong>{title}</strong> course
            and earned a{' '}
            <strong>
              {medal?.charAt(0)?.toUpperCase() + medal?.slice(1)} Dragon Mastery
              Medal
            </strong>
            .
          </p>

          <div className="my-6 flex justify-center">{renderMedalImage()}</div>

          <p className="text-gray-700 mb-6">
            {reportLoading ? (
              <span className="flex items-center justify-center">
                <Loader className="animate-spin mr-2 h-4 w-4" />
                Loading feedback...
              </span>
            ) : reportError ? (
              'Your dedication and hard work have paid off. Keep up the great work and continue to hone your coding skills with more courses on My Code Dragon.'
            ) : courseReport?.feedback ? (
              courseReport?.feedback?.split('\n').map((line) => (
                <p key={line} className="text-gray-700 mb-4">
                  {formatFeedback(line, medal)}
                </p>
              ))
            ) : (
              'Your dedication and hard work have paid off. Keep up the great work and continue to hone your coding skills with more courses on My Code Dragon.'
            )}
          </p>

          <div className="mt-6">{renderButtons()}</div>
        </div>
      </div>
    </div>
  );
};

export default CourseCompletion;
